import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
} from "@mui/material";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import {
  getFeatureFlags,
  toggleFeatureFlag,
  FeatureFlag,
} from "../../api/featureFlagsApi";

const StationSettingsSection: React.FC = () => {
  const { stationId } = useParams<{ stationId: string }>();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>(
    {},
  );
  // Synchronous in-flight guard: setPendingToggles is async, so back-to-back
  // clicks can both pass the pendingToggles check before the re-render lands.
  const inFlightTogglesRef = useRef<Set<string>>(new Set());
  const { showError } = useErrorSnackbar();

  useEffect(() => {
    if (!stationId) return;

    let mounted = true;
    setLoading(true);
    getFeatureFlags(stationId)
      .then((res) => {
        if (mounted) {
          setFlags(res.flags);
          setLoading(false);
        }
      })
      .catch((_) => {
        if (mounted) {
          showError("Failed to fetch feature flags");
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [stationId, showError]);

  const handleToggle = async (slug: string, currentEnabled: boolean) => {
    if (!stationId) return;
    if (inFlightTogglesRef.current.has(slug)) return;
    if (pendingToggles[slug]) return;

    inFlightTogglesRef.current.add(slug);
    const newEnabledState = !currentEnabled;
    setPendingToggles((prev) => ({ ...prev, [slug]: true }));
    // Optimistic update
    setFlags((prev) =>
      prev.map((f) => (f.slug === slug ? { ...f, enabled: newEnabledState } : f)),
    );

    try {
      await toggleFeatureFlag(stationId, slug, newEnabledState);
    } catch (err) {
      showError(`Failed to update feature flag "${slug}"`);
      // Revert optimistic update
      setFlags((prev) =>
        prev.map((f) => (f.slug === slug ? { ...f, enabled: currentEnabled } : f)),
      );
    } finally {
      inFlightTogglesRef.current.delete(slug);
      setPendingToggles((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold" marginBottom={2}>
        Edit Station Settings
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : flags.length === 0 ? (
        <Typography>No feature flags found for this station.</Typography>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {flags.map((flag, index) => (
              <React.Fragment key={flag.slug}>
                <ListItem>
                  <ListItemText
                    primary={flag.name || flag.slug}
                    secondary={flag.description}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      edge="end"
                      onChange={() => handleToggle(flag.slug, flag.enabled)}
                      checked={flag.enabled}
                      color="primary"
                      disabled={
                        !!pendingToggles[flag.slug] ||
                        inFlightTogglesRef.current.has(flag.slug)
                      }
                      inputProps={{
                        "aria-label": `Toggle ${flag.name || flag.slug}`,
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                {index < flags.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </>
  );
};

export default StationSettingsSection;
